import { memo, useMemo, useState, useCallback } from 'react';
import { Box, ButtonGroup, HStack, Heading, Text, chakra, useRecipe } from '@chakra-ui/react';
import { toggleRecipe } from '../../theme/recipes/toggle';
import { fieldFill, fieldFillHover } from '../../theme/themeColor';
import { RiftboundButton } from './button';
import { MdSearch, MdClose, MdLayersClear } from 'react-icons/md';
import { RiftboundInput } from './input';

const DEFAULT_MAX_COLLAPSED = 18;

// Approximate px height per pill row (py={0.5}*2 + 2xs line-height) + row gap.
const ROW_HEIGHT_PX = 24;
const ROW_GAP_PX = 4;

const identityLabel = (v: string) => v;

export interface PillToggleFilterProps {
  label?: string;
  options: string[];
  selected: string[];
  /** Toggle membership for one option value. */
  onToggle: (value: string) => void;
  /**
   * Clear every selected value in one update. Prefer this over toggling each
   * item — parents that rebuild state from a closed-over `filter` only apply
   * the last `onToggle` when "clear N" fires multiple updates.
   */
  onClearSelected?: () => void;
  disabled?: boolean;
  /** Show search field to narrow visible options (e.g. keywords). */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptySearchMessage?: string;
  /** Max pills before "show more" when not searching and not expanded. */
  maxCollapsed?: number;
  /**
   * Limit the collapsed pill area to this many visual rows via CSS max-height.
   * When set, all options are rendered but overflow is clipped until expanded —
   * selected items are never silently hidden.
   * Each row is ~24px + 4px gap, so maxRows={2} ≈ 52px.
   */
  maxRows?: number;
  formatOptionLabel?: (value: string) => string;
}

export const PillToggleFilter = memo(function PillToggleFilter({
  label = 'Filter',
  options,
  selected,
  onToggle,
  onClearSelected,
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search…',
  emptySearchMessage = 'No matches.',
  maxCollapsed = DEFAULT_MAX_COLLAPSED,
  maxRows,
  formatOptionLabel = identityLabel,
}: PillToggleFilterProps) {
  const toggle = useRecipe({ recipe: toggleRecipe });
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => formatOptionLabel(o).toLowerCase().includes(q));
  }, [options, query, searchable, formatOptionLabel]);

  // When maxRows is active, render all options (CSS clips); otherwise use the
  // item-count approach so selected items beyond the slice are still shown.
  const visible = useMemo(() => {
    if (maxRows) return filtered;
    if (searchable && query.trim()) return filtered;
    if (expanded) return filtered;
    const sliced = filtered.slice(0, maxCollapsed);
    const extra = filtered.slice(maxCollapsed).filter((k) => selected.includes(k));
    return extra.length > 0 ? [...sliced, ...extra] : sliced;
  }, [filtered, selected, query, expanded, searchable, maxCollapsed, maxRows]);

  const hiddenCount = filtered.length - visible.length;

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (e.target.value) setExpanded(false);
  }, []);

  const clearQuery = useCallback(() => setQuery(''), []);

  if (!options.length) return null;

  const isSearching = searchable && query.trim();
  const showCollapsedChrome = !isSearching;

  // CSS max-height for the pill area when maxRows is set and collapsed.
  // Mobile pills are 44px with an 8px gap; desktop stays on the compact row.
  const pillMaxHeight =
    maxRows && !expanded && !isSearching
      ? {
          base: `${maxRows * 44 + (maxRows - 1) * 8}px`,
          md: `${maxRows * ROW_HEIGHT_PX + (maxRows - 1) * ROW_GAP_PX}px`,
        }
      : undefined;

  return (
    <Box w="100%" display="flex" flexDirection="column" gap={2}>
      <HStack justify="space-between" align="center">
        <Heading as="h3" size="sm">
          {label}
        </Heading>
        {selected.length > 0 && (
          <chakra.button
            type="button"
            fontSize="2xs"
            color="textMuted"
            cursor="pointer"
            display="inline-flex"
            alignItems="center"
            gap={1}
            bg="transparent"
            borderWidth={0}
            p={0}
            font="inherit"
            lineHeight="inherit"
            aria-label={`Clear ${selected.length} selected`}
            _hover={{ color: 'text' }}
            onClick={() => {
              if (onClearSelected) {
                onClearSelected();
                return;
              }
              // Fallback for callers without onClearSelected. Only correct when
              // onToggle uses a functional state updater (each toggle sees the
              // previous update); otherwise only the last toggle sticks.
              selected.forEach(onToggle);
            }}
          >
            <MdLayersClear size={12} />
            clear {selected.length}
          </chakra.button>
        )}
      </HStack>

      {searchable && (
        <Box position="relative">
          <Box
            position="absolute"
            left={2}
            top="50%"
            transform="translateY(-50%)"
            color="textMuted"
            pointerEvents="none"
            display="flex"
            alignItems="center"
          >
            <MdSearch size={14} />
          </Box>
          <RiftboundInput
            placeholder={searchPlaceholder}
            value={query}
            onChange={handleQueryChange}
            pl={6}
            pr={query ? 6 : 2}
            borderRadius="md"
          />
          {query && (
            <Box
              as="button"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              color="textMuted"
              display="flex"
              alignItems="center"
              onClick={clearQuery}
              _hover={{ color: 'text' }}
            >
              <MdClose size={12} />
            </Box>
          )}
        </Box>
      )}

      {visible.length > 0 ? (
        <ButtonGroup
          w="100%"
          gap={{ base: 2, md: 1 }}
          size="sm"
          flexWrap="wrap"
          display="flex"
          overflow={pillMaxHeight ? 'hidden' : undefined}
          maxH={pillMaxHeight}
        >
          {visible.map((opt) => {
            const isSelected = selected.includes(opt);
            return (
              <RiftboundButton
                key={opt}
                variant={isSelected ? 'solid' : 'outline'}
                css={isSelected ? toggle({ tone: 'solid', selected: true }) : undefined}
                bg={isSelected ? undefined : fieldFill}
                borderColor={isSelected ? undefined : 'border'}
                _hover={isSelected ? undefined : { bg: fieldFillHover, borderColor: 'accentMuted' }}
                color={isSelected ? undefined : 'text'}
                onClick={() => onToggle(opt)}
                disabled={disabled}
                px={{ base: 3, md: 2 }}
                py={{ base: 2, md: 0.5 }}
                h="auto"
                minH={{ base: '44px', md: 'unset' }}
                fontSize={{ base: 'xs', md: '2xs' }}
                fontWeight={isSelected ? 'semibold' : 'normal'}
                flex={{ base: '1 0 auto', md: '0 0 auto' }}
              >
                {formatOptionLabel(opt)}
              </RiftboundButton>
            );
          })}
        </ButtonGroup>
      ) : (
        <Text fontSize="xs" color="textMuted">
          {emptySearchMessage}
        </Text>
      )}

      {/* Row-based collapse chrome */}
      {maxRows && showCollapsedChrome && !expanded && filtered.length > 0 && (
        <Text
          as="button"
          fontSize="xs"
          color="accent"
          cursor="pointer"
          textAlign="left"
          onClick={() => setExpanded(true)}
          _hover={{ textDecoration: 'underline' }}
        >
          Show more
        </Text>
      )}
      {maxRows && showCollapsedChrome && expanded && (
        <Text
          as="button"
          fontSize="xs"
          color="textMuted"
          cursor="pointer"
          textAlign="left"
          onClick={() => setExpanded(false)}
          _hover={{ textDecoration: 'underline' }}
        >
          Show less
        </Text>
      )}

      {/* Item-count collapse chrome (used when maxRows is not set) */}
      {!maxRows && showCollapsedChrome && hiddenCount > 0 && (
        <Text
          as="button"
          fontSize="xs"
          color="accent"
          cursor="pointer"
          textAlign="left"
          onClick={() => setExpanded(true)}
          _hover={{ textDecoration: 'underline' }}
        >
          +{hiddenCount} more
        </Text>
      )}
      {!maxRows && showCollapsedChrome && expanded && filtered.length > maxCollapsed && (
        <Text
          as="button"
          fontSize="xs"
          color="textMuted"
          cursor="pointer"
          textAlign="left"
          onClick={() => setExpanded(false)}
          _hover={{ textDecoration: 'underline' }}
        >
          Show less
        </Text>
      )}
    </Box>
  );
});
